// TenderQuotesAnalysis.jsx
// -----------------------------------------------------------------------------
// This component renders a bar chart showing aggregated tender quote data.
// It allows the user to:
//   - Compare lanes by different metrics:
//       * Best Quote (lowest BidValue)
//       * Most Quotes (number of quotes)
//       * More Loads (maximum Loads)
//       * Carriers (number of distinct carriers)
//       * Best Estimated Transit Days (lowest estimatedTransitDays)
//   - Aggregate either by Source or Destination per lane.
// It is responsive and delegates the actual chart rendering to <TenderBarChart />.
// -----------------------------------------------------------------------------

import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { MSelect } from "src/M_Components";
import TenderBarChart from "./TenderBarChart";
import { useTranslation } from "react-i18next";
import "./TenderQuotesAnalysis.css";

function TenderQuotesAnalysis({
  tenderId = null, // Tender identifier (kept for consistency/extension)
  routeId = null, // Route identifier (kept for potential future use)
  isSpotTender, // Flag indicating if this is a Spot tender (vs Non-Spot / Multi-phase)
  lanes = [], // Array of lane objects (LaneSource, LaneDestination, LaneId / Id, etc.)
  laneSummaries = [], // Array of aggregated rows (per lane) from /laneSummaries (already filtered server-side)
}) {

  const { t } = useTranslation();

  // ------------------------------------------------------------------------------------
  // Local UI state
  // ------------------------------------------------------------------------------------
  // `comparison` determines which metric is used to compute the bar height.
  const [comparison, setComparison] = useState("Best Quote");
  // `sourceOrDest` determines whether we aggregate lanes by Source or Destination.
  const [sourceOrDest, setSourceOrDest] = useState("Destination");

  // ------------------------------------------------------------------------------------
  // Comparison options
  // - Memoized so that MSelect receives stable references (avoids unnecessary rerenders).
  // - Includes "Best Estimated Transit Days" using the minimum of estimatedTransitDays.
  // ------------------------------------------------------------------------------------
  const comparisonOptions = useMemo(() => {
    const hasCarrierCounts = (laneSummaries || []).some((s) => {
      const v =
        s?.DistinctCarriers ??
        s?.distinctCarriers ??
        s?.DistinctCarriersCount ??
        s?.distinctCarriersCount ??
        null;
      return v != null && Number.isFinite(Number(v));
    });

    const base = [
      { label: t("Best Quote"), value: "Best Quote" },
      { label: t("Most Quotes"), value: "Most Quotes" },
      { label: t("More Loads"), value: "More Loads" },
      {
        label: t("Best Estimated Transit Days"),
        value: "Best Estimated Transit Days",
      },
    ];

    // Only show "Carriers" if the backend provides a meaningful field in laneSummaries.
    if (hasCarrierCounts) {
      base.splice(3, 0, { label: t("Carriers"), value: "Carriers" });
    }

    return base;
  }, [t, laneSummaries]);

  // ------------------------------------------------------------------------------------
  // Source/Destination options
  // - Also memoized for the same reason (stable references).
  // ------------------------------------------------------------------------------------
  const sourceOptions = useMemo(
    () => [
      { label: t("Source"), value: "Source" },
      { label: t("Destination"), value: "Destination" },
    ],
    [t]
  );

  // ------------------------------------------------------------------------------------
  // laneSummaries are already filtered/paged server-side (Inactive=false, TenderId, and (if non-spot) TenderPhaseId).
  // This component MUST NOT filter or sort on the FE.
  // ------------------------------------------------------------------------------------
  const noData = !lanes.length || !(laneSummaries || []).length;

  // Build a quick lookup for O(1) joins: LaneId -> summary row.
  const summaryByLaneId = useMemo(() => {
    const map = new Map();
    for (const r of laneSummaries || []) {
      const laneId = r?.LaneId ?? r?.laneId ?? r?.Id ?? r?.id ?? null;
      if (laneId == null) continue;
      map.set(Number(laneId), r);
    }
    return map;
  }, [laneSummaries]);


  // ------------------------------------------------------------------------------------
  // Compute chartData:
  // - Pure function, memoized to avoid recalculations on unrelated rerenders.
  // - Returns an object of shape: { [label: string]: number }
  //   where "label" is either Source or Destination, and the number is the metric chosen.
  //
  // Steps:
  //   1) Group quotes by laneId.
  //   2) For each lane, derive a label (Source or Destination).
  //   3) Aggregate counts, minBid, maxLoads, minEtd, carriers per label.
  //   4) Convert aggregated map into a plain object for the chart.
  // ------------------------------------------------------------------------------------
  const chartData = useMemo(() => {
    if (noData) return {};

    // Aggregate per label (Source/Destination)
    const result = new Map();

    for (const lane of lanes) {
      const laneKeyRaw =
        lane?.LaneId ?? lane?.Id ?? lane?.laneId ?? lane?.id ?? null;
      const laneKey = laneKeyRaw != null ? Number(laneKeyRaw) : null;

      const label =
        sourceOrDest === "Source"
          ? (lane?.LaneSource ??
            lane?.sourceLocation ??
            lane?.Origin ??
            lane?.origin ??
            "").trim()
          : (lane?.LaneDestination ??
            lane?.destinationLocation ??
            lane?.Destination ??
            lane?.destination ??
            "").trim();

      if (!label || laneKey == null || !Number.isFinite(laneKey)) continue;

      const s = summaryByLaneId.get(laneKey);
      if (!s) continue;

      if (!result.has(label)) {
        result.set(label, {
          totalQuotes: 0,
          minBid: Number.POSITIVE_INFINITY,
          maxLoads: 0,
          minEtd: Number.POSITIVE_INFINITY,

          // Optional: if BE ever includes per-lane distinct carriers count, we can use it here.
          // NOTE: summing distinct counts across lanes can overcount carriers if they appear in multiple lanes
          // under the same label. For now we only enable this metric if the field exists.
          carriersCountSum: 0,
          carriersCountAvailable: false,
        });
      }

      const agg = result.get(label);

      const totalQuotes = Number(s?.TotalQuotes ?? s?.totalQuotes ?? 0);
      const minBid = Number(
        s?.MinBidValue ?? s?.minBidValue ?? Number.POSITIVE_INFINITY
      );
      const maxLoads = Number(s?.MaxLoads ?? s?.maxLoads ?? 0);
      const minEtd = Number(
        s?.MinEstimatedTransitDays ??
        s?.minEstimatedTransitDays ??
        Number.POSITIVE_INFINITY
      );

      if (Number.isFinite(totalQuotes)) agg.totalQuotes += totalQuotes;
      if (Number.isFinite(minBid)) agg.minBid = Math.min(agg.minBid, minBid);
      if (Number.isFinite(maxLoads))
        agg.maxLoads = Math.max(agg.maxLoads, maxLoads);
      if (Number.isFinite(minEtd)) agg.minEtd = Math.min(agg.minEtd, minEtd);

      const carriersCount =
        s?.DistinctCarriers ??
        s?.distinctCarriers ??
        s?.DistinctCarriersCount ??
        s?.distinctCarriersCount;
      if (carriersCount != null && Number.isFinite(Number(carriersCount))) {
        agg.carriersCountAvailable = true;
        agg.carriersCountSum += Number(carriersCount);
      }
    }

    // Build final chart data object based on the selected comparison metric
    const finalChart = {};

    for (const [label, data] of result.entries()) {
      let value = 0;

      switch (comparison) {
        case "Best Quote":
          value = data.minBid !== Number.POSITIVE_INFINITY ? data.minBid : 0;
          break;

        case "Most Quotes":
          value = data.totalQuotes;
          break;

        case "More Loads":
          value = data.maxLoads;
          break;

        case "Carriers":
          // Only meaningful if BE provides a per-lane distinct-carriers count.
          value = data.carriersCountAvailable ? data.carriersCountSum : 0;
          break;

        case "Best Estimated Transit Days":
          value = data.minEtd !== Number.POSITIVE_INFINITY ? data.minEtd : 0;
          break;

        default:
          value = 0;
      }

      finalChart[label] = value;
    }

    return finalChart;
  }, [lanes, comparison, sourceOrDest, summaryByLaneId, noData]);

  // ------------------------------------------------------------------------------------
  // Render
  // - Uses CSS classes from TenderQuotesAnalysis.css for styling + responsiveness.
  // - Shows two selects (Comparison / Source) and the chart (or a "no data" message).
  // ------------------------------------------------------------------------------------
  return (
    <div className="tqa-root">
      {/* Section title */}
      <span className="tqa-title">{t("Analysis Responses")}</span>

      {/* Controls row/column: Comparison & Source/Destination */}
      <div className="tqa-controls">
        {/* Comparison selection */}
        <div className="tqa-control">
          <MSelect
            label={t("Comparison")}
            options={comparisonOptions}
            value={
              comparisonOptions.find((opt) => opt.value === comparison) || null
            }
            onChangeValue={(selected) => {
              if (!selected) {
                setComparison("Best Quote");
                return;
              }
              setComparison(selected.value);
            }}
            autocomplete
            required
          />
        </div>

        {/* Source or Destination selection */}
        <div className="tqa-control">
          <MSelect
            label={t("Source")}
            options={sourceOptions}
            value={
              sourceOptions.find((opt) => opt.value === sourceOrDest) || null
            }
            onChangeValue={(selected) => {
              if (!selected) {
                setSourceOrDest("Destination");
                return;
              }
              setSourceOrDest(selected.value);
            }}
            autocomplete
            required
          />
        </div>
      </div>

      {/* Main chart area or "no data" message */}
      {!noData ? (
        <div className="tqa-chart-wrapper">
          <div className="tqa-chart-inner">
            <TenderBarChart data={chartData} />
          </div>
        </div>
      ) : (
        <div className="tqa-no-data">
          <span className="tqa-no-data-text">{t("No data available")}</span>
        </div>
      )}
    </div>
  );
}

TenderQuotesAnalysis.propTypes = {
  tenderId: PropTypes.number,
  routeId: PropTypes.number,
  isSpotTender: PropTypes.bool.isRequired,
  lanes: PropTypes.array.isRequired,
  laneSummaries: PropTypes.array.isRequired,
};

// React.memo: avoid rerenders if parent rerenders but props remain shallow-equal
export default React.memo(TenderQuotesAnalysis);
